import type { WorkItem } from "@/lib/content/work";

/**
 * Where a portfolio item is published.
 *
 * - `public` — listed on `/work` (and in the public Work API scope)
 * - `api-only` — bearer token Work API + campaign LPs; not on `/work`
 * - `internal` — in source only; hidden from site and API
 */
export type WorkPublicationStatus = "public" | "api-only" | "internal";

/** Real case study vs LP/API placeholder shell. */
export type WorkPortfolioTier = "case-study" | "architectural";

export function resolveWorkPublicationStatus(
  item: Pick<WorkItem, "listed" | "campaignLpOnly" | "publicationStatus">,
): WorkPublicationStatus {
  if (item.publicationStatus) {
    return item.publicationStatus;
  }
  if (item.listed === true) {
    return "public";
  }
  if (item.campaignLpOnly === true) {
    return "api-only";
  }
  return "internal";
}

export function workItemVisibleOnPublicSite(item: WorkItem): boolean {
  return resolveWorkPublicationStatus(item) === "public";
}

export function workItemVisibleInWorkApi(
  item: WorkItem,
  scope: "public" | "full",
): boolean {
  const status = resolveWorkPublicationStatus(item);
  if (status === "internal") {
    return false;
  }
  if (scope === "full") {
    return status === "public" || status === "api-only";
  }
  return status === "public";
}

export function resolveWorkPortfolioTier(item: {
  portfolioTier?: WorkPortfolioTier;
  listed?: boolean;
  featuredMedia: { url: string };
}): WorkPortfolioTier {
  if (item.portfolioTier) {
    return item.portfolioTier;
  }
  if (item.featuredMedia.url.includes("card-texture")) {
    return "architectural";
  }
  return "case-study";
}
